<!--
  ZoneTable part — the horizontal scrollbar row. INTERNAL: not a Zone component, no story, not in
  llms.txt. Rendered only by Table.vue, whose guide documents the behaviour under SCROLL.

  Zone draws its own scrollbar rather than styling the native one: the native bar is
  platform-dependent (invisible until scrolled on macOS, a chunky gutter on Windows) and the Figma
  spec asks for one fixed look — an 8px gray-400 thumb on its own row (the Figma "Scroll" anatomy
  element), present only while the table overflows. The scroll container keeps `overflow-x-auto`,
  so native panning still works and this bar is a second, always-visible handle onto the same
  scrollLeft.

  It measures a container it does not own: the element it reports on is Table.vue's scroll
  container, a SIBLING in the DOM rather than a descendant. So Table.vue passes a `getContainer`
  GETTER, not the element — a reactive element prop would only reach this component a tick after
  the parent's `mounted()` set it, which is a tick every caller of `sync()` would have to know to
  wait out. A getter is read at measure time and is always current.

  Table.vue drives the lifecycle explicitly (`observe()` from its `mounted()`, `teardown()` from
  its `beforeUnmount()`, `scheduleSync()` from its `updated()`, `sync()` from its `@scroll`), for
  the same reason: a child mounts BEFORE its parent, so at our own `mounted()` the container does
  not exist yet.
-->

<template>
  <div
    v-show="visible"
    class="px-2 py-1"
    data-testid="table-scrollbar"
  >
    <div
      ref="track"
      class="relative h-2"
    >
      <div
        class="absolute top-0 h-2 cursor-grab rounded-full bg-gray-400 transition-colors hover:bg-gray-500 active:cursor-grabbing"
        :style="{ width: `${thumbWidth}px`, left: `${thumbLeft}px` }"
        data-testid="table-scrollbar-thumb"
        @pointerdown="startDrag"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { nextTick, ref, type PropType } from 'vue';

/** Apple's minimum touch target — the thumb never shrinks below it, however wide the table gets. */
const MIN_THUMB_WIDTH = 44;

interface DragState {
  pointerId: number;
  startX: number;
  startScrollLeft: number;
  maxThumbTravel: number;
}
</script>

<script setup lang="ts">
defineOptions({ name: 'ZoneTableScrollbar' });

const props = defineProps({
  /**
   * Returns the element whose overflow this bar reports on, or null before it exists. A getter
   * rather than the element itself — see the note at the top of this file.
   */
  getContainer: {
    type: Function as PropType<() => HTMLElement | null | undefined>,
    required: true,
  },
});

const track = ref<HTMLElement | null>(null);

const visible = ref(false);
const thumbWidth = ref(0);
const thumbLeft = ref(0);
let resizeObserver: ResizeObserver | null = null;
let syncFrame: number | null = null;
let drag: DragState | null = null;

/**
 * Measuring across the hidden -> shown edge needs a tick in between.
 *
 * The root is `v-show`n, so while the bar is hidden it is `display: none` and EVERY
 * descendant — the track included — measures 0. `visible` is set in this same call, and a
 * reactive assignment does not reach the DOM synchronously, so reading `track.clientWidth`
 * straight afterwards still reads the hidden layout: 0. `Math.min(..., 0)` then collapses
 * the thumb to nothing, leaving an empty bar on first overflow until some later resize or
 * scroll happened to re-measure it. In jsdom there is no ResizeObserver, so nothing came.
 *
 * Only that one edge awaits a tick. A scroll while the bar is already visible measures
 * synchronously, as before — layout is clean inside a scroll event and deferring there
 * would visibly lag the thumb (see `scheduleSync`).
 */
async function sync() {
  const container = props.getContainer();

  if (!container || !track.value) {
    return;
  }

  const overflow = container.scrollWidth - container.clientWidth;
  const wasHidden = !visible.value;

  visible.value = overflow > 0;

  if (!visible.value) {
    return;
  }

  if (wasHidden) {
    await nextTick();

    // A concurrent sync (a resize mid-tick) may have hidden the bar again, and the track
    // would be back to measuring 0.
    if (!visible.value) {
      return;
    }
  }

  const trackWidth = track.value.clientWidth;
  const proportional = trackWidth * (container.clientWidth / container.scrollWidth);
  // Clamped to Apple's 44px minimum touch target, and never wider than the track itself.
  const width = Math.min(Math.max(proportional, MIN_THUMB_WIDTH), trackWidth);

  thumbWidth.value = width;
  thumbLeft.value = (container.scrollLeft / overflow) * (trackWidth - width);
}

/**
 * Coalesces measurement into one frame. Table.vue's `scroll` handler calls `sync()` directly
 * instead — layout is already clean inside a scroll event, and deferring there would visibly
 * lag the thumb.
 */
function scheduleSync() {
  if (syncFrame !== null || typeof requestAnimationFrame === 'undefined') {
    return;
  }

  syncFrame = requestAnimationFrame(() => {
    syncFrame = null;
    sync();
  });
}

function onDrag(event: PointerEvent) {
  const container = props.getContainer();

  if (!container || !drag || drag.maxThumbTravel <= 0) {
    return;
  }

  const overflow = container.scrollWidth - container.clientWidth;
  const moved = ((event.clientX - drag.startX) / drag.maxThumbTravel) * overflow;

  // Assigning scrollLeft fires `scroll`, which calls sync() and moves the thumb.
  container.scrollLeft = Math.max(0, Math.min(overflow, drag.startScrollLeft + moved));
}

function endDrag() {
  if (!drag) {
    return;
  }

  drag = null;
  window.removeEventListener('pointermove', onDrag);
  window.removeEventListener('pointerup', endDrag);
  window.removeEventListener('pointercancel', endDrag);
}

function startDrag(event: PointerEvent) {
  const container = props.getContainer();

  if (!container || !track.value) {
    return;
  }

  event.preventDefault();

  const thumb = event.currentTarget as HTMLElement;

  thumb.setPointerCapture?.(event.pointerId);

  drag = {
    pointerId: event.pointerId,
    startX: event.clientX,
    startScrollLeft: container.scrollLeft,
    maxThumbTravel: track.value.clientWidth - thumbWidth.value,
  };

  window.addEventListener('pointermove', onDrag);
  window.addEventListener('pointerup', endDrag);
  window.addEventListener('pointercancel', endDrag);
}

function teardown() {
  resizeObserver?.disconnect();
  resizeObserver = null;

  if (syncFrame !== null) {
    cancelAnimationFrame(syncFrame);
    syncFrame = null;
  }

  endDrag();
}

function observe() {
  teardown();
  sync();

  const container = props.getContainer();

  // jsdom has no ResizeObserver, and neither do very old browsers. Losing resize-driven
  // resync is a cosmetic degradation, not a reason to blow up on mount.
  if (typeof ResizeObserver === 'undefined' || !container) {
    return;
  }

  resizeObserver = new ResizeObserver(() => scheduleSync());
  resizeObserver.observe(container);

  // The <table> too: columns can widen without the container changing size at all.
  const table = container.querySelector('table');

  if (table) {
    resizeObserver.observe(table);
  }
}

// Table.vue drives this bar through a template ref — see the SCROLL notes there.
defineExpose({ observe, teardown, sync, scheduleSync });
</script>
